import type { Logger, LogSource } from "@skaile/workspaces/types";
import { LogStore } from "./log-store.js";
import { normalizeError, sanitizeData } from "./sanitize.js";
import { NoOpSink } from "./sinks/no-op-sink.js";
import { StdoutSink } from "./sinks/stdout-sink.js";

/**
 * The active LogStore is a process-singleton. It is stored on `globalThis`
 * under a `Symbol.for` key so it survives module duplication — under vitest's
 * `skaile-src` aliasing and the runner's lazy `import("@skaile/workspaces/runner")`,
 * `core/logging` can be evaluated as two distinct module instances. A plain
 * module-scoped `let` would give each instance its own store, so a logger
 * created on one instance would not see a store registered on the other.
 * The `globalThis` slot makes the singleton genuinely process-wide.
 */
const ACTIVE_STORE_KEY = Symbol.for("@skaile/workspaces:logging:activeStore");

type GlobalWithStore = typeof globalThis & {
  [ACTIVE_STORE_KEY]?: LogStore | null;
};

const globalStoreSlot = globalThis as GlobalWithStore;

export function registerLogStore(store: LogStore): void {
  globalStoreSlot[ACTIVE_STORE_KEY] = store;
}

export function getLogStore(): LogStore | null {
  return globalStoreSlot[ACTIVE_STORE_KEY] ?? null;
}

export function resetLogStore(): void {
  globalStoreSlot[ACTIVE_STORE_KEY] = null;
}

export interface CreateLoggerOptions {
  /**
   * When true (default), create + register a lazy off-mode LogStore if none
   * is registered. When false, throw if no store has been registered.
   */
  fallback?: boolean;
}

function ensureLogStore(): LogStore {
  const existing = globalStoreSlot[ACTIVE_STORE_KEY];
  if (existing) return existing;
  // Lazy off-mode fallback: NoOp + Stdout, no SQLite, no sessionId.
  const fallback = new LogStore({
    sessionId: "no-session",
    level: "info",
    sinks: [new NoOpSink(), new StdoutSink()],
  });
  globalStoreSlot[ACTIVE_STORE_KEY] = fallback;
  return fallback;
}

/**
 * Creates a logger for the given source.
 *
 * If no LogStore is registered and fallback is enabled (default), a lazy
 * off-mode fallback store (NoOp + Stdout) is created.
 *
 * @param source - The log source (e.g., { kind: 'agent', name: 'my-agent' })
 * @param opts - Configuration options for logger creation
 * @param opts.fallback - When true (default), creates a fallback store if none registered
 * @returns A logger instance
 * @throws {Error} When no LogStore is registered and fallback is false
 *
 * @example
 * ```typescript
 * const logger = createLogger({ kind: 'agent', name: 'my-agent' });
 * logger.info('Agent started');
 * ```
 */
export function createLogger(source: LogSource, opts: CreateLoggerOptions = {}): Logger {
  const fallback = opts.fallback !== false;
  if (!getLogStore() && !fallback) {
    throw new Error("createLogger: no LogStore registered. Call registerLogStore() first.");
  }
  const store = ensureLogStore();
  return makeLogger(source, store);
}

function makeLogger(source: LogSource, store: LogStore): Logger {
  const emit = (
    level: "debug" | "info" | "warn" | "error",
    message: string,
    data?: object,
    err?: unknown,
  ) => {
    if (!store.isEnabled(level)) return;
    store.record({
      source,
      level,
      message,
      data: sanitizeData(data),
      error: normalizeError(err),
    });
  };
  return {
    debug(message, data) {
      emit("debug", message, data);
    },
    info(message, data) {
      emit("info", message, data);
    },
    warn(message, data) {
      emit("warn", message, data);
    },
    error(message, err, data) {
      emit("error", message, data, err);
    },
    child(partial) {
      return makeLogger({ ...source, ...partial }, store);
    },
  };
}
